import {
  Container,
  createMuiTheme,
  CssBaseline,
  MuiThemeProvider,
  Typography,
} from '@material-ui/core';
import { KeycloakUser, UserProvider } from './user';
import { APIProvider } from '@lyonfuture/api';
import ProductProvider from '../components/ProductProvider';
import AppToast from '../components/AppToast';
import Routes from '../routes/Routes';
import { PrimaryButton } from '../components/Button';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { AxiosRequestConfig } from 'axios';
import { useKeycloak } from '@react-keycloak/web';
import { Role } from './enums/role';
import PaddedPaper from '../components/PaddedPaper';

const kcClient = process.env.KEYCLOAK_CLIENT ?? '';

const theme = createMuiTheme({
  typography: {
    fontFamily: `"Lato", sans-serif`,
  },
});

const API_CONFIG = {
  baseURL: `${process.env.API_BASE_URL}`,
};

const NoAccessContainer = styled(Container)`
  &&& {
    margin-top: 60px;

    .MuiTypography-body1.MuiTypography-gutterBottom {
      margin-bottom: 30px;
    }
  }
`;

export const AppProvider = () => {
  const { keycloak } = useKeycloak();
  const { token, tokenParsed, logout } = keycloak;

  const authInterceptor = useCallback(
    (config: AxiosRequestConfig): AxiosRequestConfig => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const subscriptionKey = process.env.APIM_SUBSCRIPTION_KEY;
      if (subscriptionKey) {
        config.headers = config.headers ?? {};
        config.headers['Ocp-Apim-Subscription-Key'] = subscriptionKey;
      }
      return config;
    },
    [token]
  );

  const roles: string[] = tokenParsed?.resource_access?.[kcClient]?.roles ?? [];
  const isAuthor = roles.includes(Role.AUTHOR);
  const isOwner = !isAuthor && roles.includes(Role.ADMIN);
  const isBHTConsultant = !isOwner && roles.includes(Role.BHT_CONSULTANT);

  const userCanAccess = isAuthor || isOwner || isBHTConsultant;
  const user: KeycloakUser = {
    _isAuthor: isAuthor,
    _isOwner: isOwner,
    _isBHTConsultant: isBHTConsultant,
  };

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {userCanAccess ? (
        <UserProvider user={user}>
          <APIProvider
            config={API_CONFIG}
            httpRequestInterceptor={authInterceptor}
          >
            <ProductProvider>
              <AppToast>
                <Routes />
              </AppToast>
            </ProductProvider>
          </APIProvider>
        </UserProvider>
      ) : (
        <NoAccessContainer>
          <PaddedPaper>
            <Typography variant="h4" gutterBottom>
              Sorry!
            </Typography>
            <Typography variant="body1" gutterBottom>
              You have no access to this application.
            </Typography>
            <PrimaryButton onClick={logout}>Log Out</PrimaryButton>
          </PaddedPaper>
        </NoAccessContainer>
      )}
    </MuiThemeProvider>
  );
};
